import React, { useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ShareButtons from './ShareButtons';

/**
 * Jackson State Women's Soccer - Built for the SWAC Race
 * A premium interactive article featuring the 2026 Jackson State Tigers
 * 
 * All CSS classes prefixed with jsu-soccer- to prevent style bleeding
 */
const JacksonStateSoccerArticle = () => {
  const rosterRef = useRef(null);
  const coachRef = useRef(null);

  // Scroll carousel by 75-80% of visible width
  const scrollCarousel = useCallback((ref, direction) => {
    if (ref.current) {
      const containerWidth = ref.current.offsetWidth;
      const scrollAmount = containerWidth * 0.78 * direction;
      ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, []);

  // Smooth scroll to section
  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for sticky nav
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  }, []);

  // Full 2026 roster with official JSU Athletics images
  const players = [
    { num: '0', pos: 'GK', name: 'Kamyia Fultz', year: 'Freshman', hometown: 'Ridgeland, Miss.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/9/8/_N0A9325__2_.JPG' },
    { num: '1', pos: 'GK', name: 'Taylor Osborn', year: 'Sophomore', hometown: 'Port St. Lucie, Fla.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6244_JvfEL.JPG' },
    { num: '2', pos: 'DEF/MID', name: 'Sarah Portis', year: 'Senior', hometown: 'Anaheim, Calif.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6787_OGY8W.JPG' },
    { num: '3', pos: 'DEF', name: 'Kianah Farmer', year: 'Junior', hometown: 'Orange County, Calif.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6781_89Lkb.JPG' },
    { num: '4', pos: 'DEF', name: 'Asia Wilson', year: 'Junior', hometown: 'Las Vegas, Nev.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6764_pDrUt.JPG' },
    { num: '5', pos: 'DEF', name: 'Samiah Thompson', year: 'Freshman', hometown: 'Norton, Ohio', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6221_6YqBH.JPG' },
    { num: '6', pos: 'FOR/MID', name: 'Josetiarainnah Olaco', year: 'Senior', hometown: 'Dartmouth, Nova Scotia', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6852_zXuRA.JPG' },
    { num: '7', pos: 'MID', name: 'Ryanne Hawthorn-Harper', year: 'Graduate Student', hometown: 'Mississauga, Ontario', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6809_5NfbR.JPG' },
    { num: '8', pos: 'MID', name: 'Myla Hunter', year: 'Freshman', hometown: 'St. Louis, Mo.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6216_MPCjE.JPG' },
    { num: '9', pos: 'MID', name: 'Nahomy Portillo', year: 'Freshman', hometown: 'Houston, Texas', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6198_xPbna.JPG' },
    { num: '10', pos: 'MID', name: 'Karime Antonio-Lopez', year: 'Senior', hometown: 'Murray, Utah', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6850_yYABT.JPG' },
    { num: '11', pos: 'FOR/MID', name: 'Mia Ortega', year: 'Sophomore', hometown: 'Chicago, Ill.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6800_0mZLA.JPG' },
    { num: '12', pos: 'FOR', name: 'Akeelah Mirza', year: 'Sophomore', hometown: 'Brampton, Ontario', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6229_1kUNv.JPG' },
    { num: '13', pos: 'FOR/MID', name: 'Malayah Nuestro', year: 'Junior', hometown: 'Lakewood, Calif.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2025/8/3/_N0A9451.jpg' },
    { num: '14', pos: 'FOR', name: 'Elon Sheffield', year: 'Senior', hometown: 'Atlanta, Ga.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2025/8/3/_N0A9332.jpg' },
    { num: '15', pos: 'FOR/MID', name: 'Kyla Gomez', year: 'Senior', hometown: 'Long Beach, Calif.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A9862_8IwV0.JPG' },
    { num: '16', pos: 'MID', name: 'Nataline Reyes Fazio', year: 'Freshman', hometown: 'Oshawa, Ontario', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6237_BMU0u.JPG' },
    { num: '17', pos: 'FOR', name: 'Nina Kammertons', year: 'Junior', hometown: 'Bielefeld, Germany', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2025/8/3/_N0A9407.jpg' },
    { num: '18', pos: 'DEF', name: 'Laila Anderson', year: 'Senior', hometown: 'Jackson, Miss.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6822_pbwIk.JPG' },
    { num: '19', pos: 'MID', name: 'Alana DiPaola', year: 'Freshman', hometown: 'Markham, Ontario', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6207_fEKLu.JPG' },
    { num: '20', pos: 'FOR', name: "Che'-Chulae Dowling", year: 'Senior', hometown: 'Sandys, Bermuda', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2025/8/3/_N0A9380.jpg' },
    { num: '21', pos: 'DEF', name: 'Amayah Singleton', year: 'Senior', hometown: 'Wake Forest, N.C.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6761_jj1WJ.JPG' },
    { num: '22', pos: 'FOR/DEF', name: 'Maeve Morris', year: 'Senior', hometown: 'Salem, Ore.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6753_qkZvj.JPG' },
    { num: '23', pos: 'FOR/MID', name: 'Dezire Kaila Tablado', year: 'Senior', hometown: 'Phoenix, Ariz.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6844_56OYU.JPG' },
    { num: '24', pos: 'DEF', name: 'Naima Calvo', year: 'Freshman', hometown: 'El Paso, Texas', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6191_EiRrS.JPG' },
    { num: '25', pos: 'DEF', name: 'Cora Kmiec', year: 'Sophomore', hometown: 'Eagan, Minn.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6189_KBkqp.JPG' },
    { num: '26', pos: 'DEF', name: 'Mariah Burnett', year: 'Junior', hometown: 'Renton, Wash.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6776_Kelzu.JPG' },
    { num: '27', pos: 'FOR', name: 'India Wilson', year: 'Junior', hometown: 'Las Vegas, Nev.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6773_7Myom.JPG' },
    { num: '28', pos: 'DEF', name: 'Makayla Sturkey', year: 'Junior', hometown: 'Jamul, Calif.', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6759_wwnPh.JPG' },
    { num: '29', pos: 'MID', name: 'Heena Rai', year: 'Sophomore', hometown: 'Langley, British Columbia', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/8/11/_N0A6251_JcpE8.JPG' },
  ];

  // Coaching staff
  const coaches = [
    { title: 'Head Coach', name: 'Dr. Ted Flogaites', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2021/1/17/DrTedFlogaites.jpg' },
    { title: 'Assistant Coach', name: 'Taylor Leddy', img: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2025/8/17/_U8A8719.jpg' },
  ];

  // Non-conference schedule
  const games = [
    { date: 'August 12', loc: 'Home', opp: 'Mercer', result: 'L, 1-2', type: 'loss', desc: 'The opener: Jackson State opened 2026 with a competitive one-goal matchup at home.' },
    { date: 'August 15', loc: 'Neutral Site', opp: 'Abilene Christian', result: 'L, 1-3', type: 'loss', desc: 'Neutral-site challenge: Jackson State left home early in the season for a matchup against Abilene Christian.' },
    { date: 'August 20', loc: 'Away', opp: 'McNeese', result: 'L, 0-2', type: 'loss', desc: 'Road pressure: Another Division I road test challenged the Tigers\' defensive organization and composure.' },
    { date: 'August 23', loc: 'Away', opp: 'ULM', result: 'L, 1-5', type: 'loss', desc: 'Back-to-back road work: Jackson State stayed on the road and gained more experience against unfamiliar opposition.' },
    { date: 'August 30', loc: 'Home', opp: 'Tougaloo', result: 'Canceled', type: 'canceled', desc: 'This matchup was included in the original non-conference schedule but was ultimately canceled.' },
    { date: 'September 4', loc: 'Away', opp: 'Arkansas', result: 'L, 0-5', type: 'loss', desc: 'SEC measuring stick: Jackson State traveled to Fayetteville for a difficult road test against an SEC opponent.' },
    { date: 'September 7', loc: 'Home', opp: 'Delta State', result: 'W, 5-1', type: 'win', desc: 'The attack breaks through: Jackson State erupted for five goals and earned its first win of the season.' },
    { date: 'September 13', loc: 'Away', opp: 'Stephen F. Austin', result: 'L, 2-4', type: 'loss', desc: 'Another difficult road environment: Jackson State scored twice in Nacogdoches but came up short.' },
    { date: 'September 18', loc: 'Home', opp: 'Austin Peay', result: 'W, 3-1', type: 'win', desc: 'Second-half takeover: The match was tied 1-1 at halftime before Jackson State scored twice after the break.' },
    { date: 'September 20', loc: 'Home', opp: 'Middle Tennessee', result: 'W, 4-1', type: 'win', desc: 'Statement finish: Middle Tennessee entered Jackson 7-1-2. Jackson State responded with four goals and finished the non-conference schedule with major momentum.' },
  ];

  // Navigation items for the article
  const navItems = [
    { id: 'jsu-story', label: 'THE STORY' },
    { id: 'jsu-gauntlet', label: 'NON-CONFERENCE GAUNTLET' },
    { id: 'jsu-sweep', label: 'SOUTHERN SWEEP' },
    { id: 'jsu-roster', label: 'ROSTER & COACHES' },
  ];

  // Action photos spread throughout the article - official JSU Athletics game photos
  const actionPhotos = {
    austinPeay: {
      src: 'https://gojsutigers.com/images/2026/9/18/_.png?width=530&height=298&mode=crop&scale=both&format=jpg&quality=80',
      alt: 'Jackson State Soccer vs Austin Peay',
      credit: 'Jackson State Athletics / GoJSUTigers.com'
    },
    southern: {
      src: 'https://gojsutigers.com/images/2026/9/27/E56A3867.jpg?width=1060&height=596&mode=crop&scale=both&format=jpg&quality=80',
      alt: 'Jackson State Soccer vs Southern - SWAC Opener',
      credit: 'Jackson State Athletics / GoJSUTigers.com'
    }
  };

  return (
    <div className="jsu-soccer-article" data-testid="jsu-soccer-article">
      {/* Scoped Styles */}
      <style>{`
        .jsu-soccer-article {
          --jsu-navy: #071A3D;
          --jsu-blue: #0D4F9C;
          --jsu-light-bg: #F2F6FB;
          --jsu-white: #FFFFFF;
          --jsu-border: #DCE5EF;
          --jsu-muted: #637083;
          --jsu-red: #CF1838;
          --jsu-green: #16834A;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: var(--jsu-light-bg);
          min-height: 100vh;
        }
        
        .jsu-soccer-hero {
          position: relative;
          width: 100%;
          height: 500px;
          background: linear-gradient(135deg, var(--jsu-navy) 0%, #0a2a5c 100%);
          overflow: hidden;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-hero {
            height: 420px;
          }
        }
        
        .jsu-soccer-hero-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
        }
        
        .jsu-soccer-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, var(--jsu-navy) 0%, rgba(7, 26, 61, 0.7) 40%, transparent 100%);
        }
        
        .jsu-soccer-hero-content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 2.5rem;
          color: white;
          max-width: 900px;
        }
        
        @media (max-width: 640px) {
          .jsu-soccer-hero-content {
            padding: 1.5rem;
          }
        }
        
        .jsu-soccer-nav {
          position: sticky;
          top: 0;
          z-index: 50;
          background: var(--jsu-white);
          border-bottom: 1px solid var(--jsu-border);
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        
        .jsu-soccer-nav::-webkit-scrollbar {
          display: none;
        }
        
        .jsu-soccer-nav-inner {
          display: flex;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          max-width: 1100px;
          margin: 0 auto;
        }
        
        .jsu-soccer-nav-pill {
          padding: 0.625rem 1.25rem;
          border-radius: 9999px;
          font-size: 0.8125rem;
          font-weight: 600;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          background: #e8edf3;
          color: var(--jsu-muted);
        }
        
        .jsu-soccer-nav-pill:hover {
          background: #dce3eb;
          color: var(--jsu-navy);
        }
        
        .jsu-soccer-nav-pill:focus {
          outline: 2px solid var(--jsu-blue);
          outline-offset: 2px;
        }
        
        .jsu-soccer-content {
          max-width: 1100px;
          margin: 0 auto;
          padding: 2.5rem 1.5rem;
        }
        
        .jsu-soccer-section {
          margin-bottom: 4rem;
        }
        
        .jsu-soccer-heading {
          font-size: 1.875rem;
          font-weight: 700;
          color: var(--jsu-navy);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }
        
        @media (max-width: 640px) {
          .jsu-soccer-heading {
            font-size: 1.5rem;
          }
        }
        
        .jsu-soccer-text {
          font-size: 1.0625rem;
          line-height: 1.75;
          color: #475569;
          margin-bottom: 1.5rem;
        }
        
        .jsu-soccer-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin: 2rem 0;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-stat-grid {
            grid-template-columns: 1fr;
          }
        }
        
        .jsu-soccer-stat-card {
          background: linear-gradient(135deg, var(--jsu-navy) 0%, #0a2a5c 100%);
          color: white;
          padding: 1.75rem;
          border-radius: 14px;
          text-align: center;
        }
        
        .jsu-soccer-stat-value {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }
        
        .jsu-soccer-stat-label {
          font-size: 0.8125rem;
          opacity: 0.85;
          line-height: 1.4;
        }
        
        .jsu-soccer-callout {
          background: #e8f0f8;
          border-left: 4px solid var(--jsu-navy);
          padding: 1.5rem;
          border-radius: 0 12px 12px 0;
          margin: 2rem 0;
        }
        
        .jsu-soccer-callout-title {
          font-weight: 700;
          color: var(--jsu-navy);
          margin-bottom: 0.5rem;
          font-size: 1rem;
        }
        
        .jsu-soccer-callout-text {
          color: #475569;
          line-height: 1.65;
          font-size: 0.9375rem;
        }
        
        .jsu-soccer-gallery {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 1rem;
          margin: 2rem 0;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-gallery {
            grid-template-columns: 1fr;
          }
        }
        
        .jsu-soccer-gallery-main {
          border-radius: 12px;
          overflow: hidden;
          height: 320px;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-gallery-main {
            height: 240px;
          }
        }
        
        .jsu-soccer-gallery-side {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .jsu-soccer-gallery-item {
          border-radius: 12px;
          overflow: hidden;
          height: 152px;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-gallery-item {
            height: 200px;
          }
        }
        
        .jsu-soccer-gallery img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top center;
        }
        
        .jsu-soccer-game-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-game-grid {
            grid-template-columns: 1fr;
          }
        }
        
        .jsu-soccer-game-card {
          background: var(--jsu-white);
          border-radius: 14px;
          padding: 1.25rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          border-top: 3px solid transparent;
        }
        
        .jsu-soccer-game-card--win {
          border-top-color: var(--jsu-green);
        }
        
        .jsu-soccer-game-card--loss {
          border-top-color: var(--jsu-red);
        }
        
        .jsu-soccer-game-card--canceled {
          border-top-color: #94a3b8;
        }
        
        .jsu-soccer-game-meta {
          font-size: 0.75rem;
          color: var(--jsu-muted);
          margin-bottom: 0.5rem;
        }
        
        .jsu-soccer-game-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }
        
        .jsu-soccer-game-opp {
          font-weight: 700;
          font-size: 1.125rem;
          color: var(--jsu-navy);
        }
        
        .jsu-soccer-game-badge {
          background: var(--jsu-navy);
          color: white;
          font-size: 0.6875rem;
          padding: 0.25rem 0.625rem;
          border-radius: 6px;
          text-transform: uppercase;
          font-weight: 600;
        }
        
        .jsu-soccer-game-result {
          font-weight: 700;
          font-size: 0.9375rem;
          margin-bottom: 0.5rem;
        }
        
        .jsu-soccer-game-result--win { color: var(--jsu-green); }
        .jsu-soccer-game-result--loss { color: var(--jsu-red); }
        .jsu-soccer-game-result--canceled { color: #94a3b8; }
        
        .jsu-soccer-game-desc {
          font-size: 0.875rem;
          color: #64748b;
          line-height: 1.5;
        }
        
        .jsu-soccer-sweep-panel {
          background: linear-gradient(135deg, var(--jsu-navy) 0%, #0a2a5c 100%);
          border-radius: 16px;
          padding: 2.5rem;
          color: white;
          margin-bottom: 2rem;
        }
        
        @media (max-width: 640px) {
          .jsu-soccer-sweep-panel {
            padding: 1.5rem;
          }
        }
        
        .jsu-soccer-sweep-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          opacity: 0.8;
          margin-bottom: 0.5rem;
        }
        
        .jsu-soccer-sweep-title {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 1rem;
          line-height: 1.25;
        }
        
        @media (max-width: 640px) {
          .jsu-soccer-sweep-title {
            font-size: 1.375rem;
          }
        }
        
        .jsu-soccer-sweep-quote {
          font-size: 1.25rem;
          font-weight: 700;
          color: #fcd34d;
          margin: 1.5rem 0;
          line-height: 1.4;
        }
        
        .jsu-soccer-score-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-top: 2rem;
        }
        
        @media (max-width: 640px) {
          .jsu-soccer-score-grid {
            grid-template-columns: 1fr;
          }
        }
        
        .jsu-soccer-score-card {
          background: rgba(255,255,255,0.1);
          padding: 1.5rem;
          border-radius: 12px;
          text-align: center;
        }
        
        .jsu-soccer-score-label {
          font-size: 0.8125rem;
          opacity: 0.8;
          margin-bottom: 0.5rem;
        }
        
        .jsu-soccer-score-value {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }
        
        .jsu-soccer-score-teams {
          font-size: 0.9375rem;
          font-weight: 600;
        }
        
        .jsu-soccer-highlight-card {
          background: var(--jsu-light-bg);
          padding: 1.25rem;
          border-radius: 12px;
          border-left: 4px solid var(--jsu-navy);
          margin-bottom: 0.75rem;
        }
        
        .jsu-soccer-highlight-name {
          font-weight: 700;
          color: var(--jsu-navy);
          margin-bottom: 0.25rem;
        }
        
        .jsu-soccer-highlight-desc {
          font-size: 0.9375rem;
          color: #64748b;
        }
        
        .jsu-soccer-carousel-wrapper {
          margin-bottom: 3rem;
        }
        
        .jsu-soccer-carousel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        
        .jsu-soccer-carousel-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          color: var(--jsu-muted);
          letter-spacing: 0.05em;
        }
        
        .jsu-soccer-carousel-title {
          font-size: 1.375rem;
          font-weight: 700;
          color: var(--jsu-navy);
        }
        
        .jsu-soccer-carousel-controls {
          display: flex;
          gap: 0.5rem;
        }
        
        .jsu-soccer-carousel-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e8edf3;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          color: var(--jsu-navy);
        }
        
        .jsu-soccer-carousel-btn:hover {
          background: #dce3eb;
        }
        
        .jsu-soccer-carousel-btn:focus {
          outline: 2px solid var(--jsu-blue);
          outline-offset: 2px;
        }
        
        .jsu-soccer-carousel {
          display: flex;
          gap: 1rem;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 1rem;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
        }
        
        .jsu-soccer-carousel::-webkit-scrollbar {
          height: 6px;
        }
        
        .jsu-soccer-carousel::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .jsu-soccer-carousel::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        
        .jsu-soccer-player-card {
          flex: 0 0 auto;
          width: 215px;
          background: var(--jsu-white);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          scroll-snap-align: start;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-player-card {
            width: 180px;
          }
        }
        
        @media (max-width: 480px) {
          .jsu-soccer-player-card {
            width: calc(85vw - 3rem);
          }
        }
        
        .jsu-soccer-player-img {
          width: 100%;
          height: 250px;
          background: #e2e8f0;
          position: relative;
          overflow: hidden;
        }
        
        @media (max-width: 768px) {
          .jsu-soccer-player-img {
            height: 220px;
          }
        }
        
        .jsu-soccer-player-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top center;
        }
        
        .jsu-soccer-player-img-fallback {
          position: absolute;
          inset: 0;
          display: none;
          align-items: center;
          justify-content: center;
          background: #e2e8f0;
          color: #94a3b8;
          font-size: 0.75rem;
          text-align: center;
          padding: 1rem;
        }
        
        .jsu-soccer-player-info {
          padding: 1rem;
        }
        
        .jsu-soccer-player-meta {
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--jsu-blue);
          margin-bottom: 0.25rem;
          text-transform: uppercase;
        }
        
        .jsu-soccer-player-name {
          font-weight: 700;
          color: #1e293b;
          font-size: 0.9375rem;
          margin-bottom: 0.25rem;
          line-height: 1.3;
        }
        
        .jsu-soccer-player-details {
          font-size: 0.75rem;
          color: var(--jsu-muted);
          line-height: 1.4;
        }
        
        .jsu-soccer-coach-card {
          flex: 0 0 auto;
          width: 270px;
          background: var(--jsu-white);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          scroll-snap-align: start;
        }
        
        @media (max-width: 480px) {
          .jsu-soccer-coach-card {
            width: calc(90vw - 3rem);
          }
        }
        
        .jsu-soccer-coach-img {
          width: 100%;
          height: 300px;
          background: #e2e8f0;
          position: relative;
          overflow: hidden;
        }
        
        .jsu-soccer-coach-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top center;
        }
        
        .jsu-soccer-coach-info {
          padding: 1.25rem;
        }
        
        .jsu-soccer-coach-title {
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--jsu-blue);
          text-transform: uppercase;
          margin-bottom: 0.25rem;
        }
        
        .jsu-soccer-coach-name {
          font-weight: 700;
          color: #1e293b;
          font-size: 1.125rem;
          margin-bottom: 0.25rem;
        }
        
        .jsu-soccer-coach-team {
          font-size: 0.8125rem;
          color: var(--jsu-muted);
        }
        
        .jsu-soccer-credit {
          font-size: 0.75rem;
          color: var(--jsu-muted);
          margin-top: 0.75rem;
        }
        
        .jsu-soccer-sources {
          background: var(--jsu-white);
          padding: 2rem;
          border-radius: 14px;
          margin-top: 3rem;
        }
        
        .jsu-soccer-sources-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--jsu-navy);
          margin-bottom: 1rem;
        }
        
        .jsu-soccer-sources-credit {
          color: #64748b;
          margin-bottom: 1rem;
          font-size: 0.9375rem;
        }
        
        .jsu-soccer-sources-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .jsu-soccer-sources-link {
          color: var(--jsu-blue);
          text-decoration: none;
          font-size: 0.875rem;
          transition: opacity 0.2s;
        }
        
        .jsu-soccer-sources-link:hover {
          text-decoration: underline;
          opacity: 0.8;
        }
        
        .jsu-soccer-inline-photo {
          margin: 2rem 0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        
        .jsu-soccer-inline-photo img {
          width: 100%;
          height: auto;
          display: block;
        }
        
        .jsu-soccer-inline-photo-credit {
          background: #f1f5f9;
          padding: 0.75rem 1rem;
          font-size: 0.75rem;
          color: var(--jsu-muted);
          text-align: right;
        }
      `}</style>

      {/* HERO SECTION */}
      <div className="jsu-soccer-hero" data-testid="jsu-soccer-hero">
        <img 
          src="https://gojsutigers.com/images/2026/9/27/E56A3867.jpg?width=1060&height=596&mode=crop&scale=both&format=jpg&quality=80"
          alt="Jackson State Women's Soccer vs Southern - SWAC Opener"
          className="jsu-soccer-hero-img"
          loading="eager"
        />
        <div className="jsu-soccer-hero-overlay" />
        <div className="jsu-soccer-hero-content">
          <p style={{ fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.15em', opacity: 0.85, marginBottom: '0.75rem' }}>
            SHELOVESTHEE • JACKSON STATE SOCCER
          </p>
          <h1 style={{ fontSize: 'clamp(2rem, 6vw, 3.25rem)', fontWeight: 700, marginBottom: '1rem', lineHeight: 1.1 }}>
            BUILT FOR THE SWAC RACE
          </h1>
          <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', opacity: 0.9, lineHeight: 1.55, maxWidth: '700px' }}>
            Jackson State challenged itself outside the conference, found its attack late in the non-conference slate, and opened SWAC play by shutting out rival Southern.
          </p>
          <p style={{ fontSize: '0.6875rem', opacity: 0.6, marginTop: '1.5rem' }}>
            Photo: Jackson State Athletics / Jackson State Soccer / GoJSUTigers.com
          </p>
        </div>
      </div>

      {/* NAVIGATION PILLS */}
      <nav className="jsu-soccer-nav" data-testid="jsu-soccer-nav">
        <div className="jsu-soccer-nav-inner">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="jsu-soccer-nav-pill"
              data-testid={`jsu-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <div className="jsu-soccer-content">
        
        {/* SECTION 1: THE STORY */}
        <section id="jsu-story" className="jsu-soccer-section" data-testid="jsu-section-story">
          <h2 className="jsu-soccer-heading">The Defending Regular-Season Champions Are Back in the Hunt</h2>
          
          <p className="jsu-soccer-text">
            Jackson State entered 2026 carrying the standard of the <strong>2025 SWAC Regular Season Championship</strong>. The Tigers finished 2025 9-7-3 overall and 6-1-1 in SWAC play, earning the regular-season conference championship and advancing to the SWAC Tournament championship match.
          </p>
          
          <p className="jsu-soccer-text">
            Rather than easing into 2026, Jackson State scheduled a demanding non-conference slate designed to prepare the team for another SWAC championship run.
          </p>

          {/* Stat Cards */}
          <div className="jsu-soccer-stat-grid" data-testid="jsu-stat-cards">
            <div className="jsu-soccer-stat-card">
              <div className="jsu-soccer-stat-value">2025</div>
              <div className="jsu-soccer-stat-label">SWAC Regular Season Champions</div>
            </div>
            <div className="jsu-soccer-stat-card">
              <div className="jsu-soccer-stat-value">10</div>
              <div className="jsu-soccer-stat-label">Scheduled Non-Conference Matchups</div>
            </div>
            <div className="jsu-soccer-stat-card">
              <div className="jsu-soccer-stat-value">7–2</div>
              <div className="jsu-soccer-stat-label">Combined Score in Final Two Non-Conference Wins</div>
            </div>
          </div>

          <p className="jsu-soccer-text">
            Jackson State's early schedule included a neutral-site matchup against Abilene Christian, road trips to McNeese, ULM, Arkansas, and Stephen F. Austin, along with several home opportunities.
          </p>
          
          <p className="jsu-soccer-text">
            The early results were uneven, but Jackson State eventually found its offensive rhythm. The Tigers defeated <strong>Delta State 5-1</strong>, followed later by a <strong>3-1 victory over Austin Peay</strong> and a statement <strong>4-1 victory over Middle Tennessee</strong>.
          </p>

          {/* Callout Box */}
          <div className="jsu-soccer-callout" data-testid="jsu-callout-mtsu">
            <div className="jsu-soccer-callout-title">WHY THE MIDDLE TENNESSEE WIN MATTERED</div>
            <p className="jsu-soccer-callout-text">
              Middle Tennessee entered Jackson with a 7-1-2 record and one of the strongest starts in program history. Jackson State responded by scoring four goals and closing the non-conference schedule with one of its strongest performances of the season.
            </p>
          </div>

          {/* Action Photo - Austin Peay Game */}
          <div className="jsu-soccer-inline-photo" data-testid="jsu-photo-austinpeay">
            <img 
              src={actionPhotos.austinPeay.src}
              alt={actionPhotos.austinPeay.alt}
              loading="lazy"
            />
            <div className="jsu-soccer-inline-photo-credit">
              Photo: {actionPhotos.austinPeay.credit}
            </div>
          </div>

          <p className="jsu-soccer-text" style={{ marginTop: '1.5rem' }}>
            The late non-conference surge also produced individual recognition. Following victories over Austin Peay and Middle Tennessee, <strong>Ryanne Hawthorn-Harper</strong> earned SWAC Offensive Player of the Week, <strong>Makayla Sturkey</strong> earned SWAC Defensive Player of the Week, and goalkeeper <strong>Taylor Osborn</strong> earned SWAC Goalkeeper of the Week.
          </p>
        </section>

        {/* SECTION 2: NON-CONFERENCE GAUNTLET */}
        <section id="jsu-gauntlet" className="jsu-soccer-section" data-testid="jsu-section-gauntlet">
          <h2 className="jsu-soccer-heading">Jackson State Tested Itself Before SWAC Play</h2>
          
          <p className="jsu-soccer-text">
            Ten non-conference matchups. Road trips across Division I. Here's how Jackson State's schedule unfolded, game by game.
          </p>

          <div className="jsu-soccer-game-grid" data-testid="jsu-game-grid">
            {games.map((game, idx) => (
              <div 
                key={idx}
                className={`jsu-soccer-game-card jsu-soccer-game-card--${game.type}`}
                data-testid={`jsu-game-${idx}`}
              >
                <div className="jsu-soccer-game-meta">{game.date} • {game.loc}</div>
                <div className="jsu-soccer-game-header">
                  <span className="jsu-soccer-game-opp">{game.opp}</span>
                  <span className="jsu-soccer-game-badge">{game.loc}</span>
                </div>
                <div className={`jsu-soccer-game-result jsu-soccer-game-result--${game.type}`}>
                  {game.result}
                </div>
                <p className="jsu-soccer-game-desc">{game.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 3: SOUTHERN SWEEP */}
        <section id="jsu-sweep" className="jsu-soccer-section" data-testid="jsu-section-sweep">
          <div className="jsu-soccer-sweep-panel">
            <p className="jsu-soccer-sweep-label">Rivalry Weekend • Jackson State vs. Southern</p>
            <h2 className="jsu-soccer-sweep-title">Football Handled Saturday. Soccer Finished the Weekend Sunday.</h2>
            
            <p style={{ fontSize: '1.0625rem', opacity: 0.9, lineHeight: 1.6, marginBottom: '1rem' }}>
              Less than 24 hours after Jackson State football defeated Southern <strong>35-17</strong> in the BoomBox Classic, Jackson State women's soccer delivered another rivalry win with a <strong>3-0 shutout</strong>.
            </p>
            
            <p className="jsu-soccer-sweep-quote">
              Jackson State opened a can of whoop ass and completed the weekend sweep of Southern.
            </p>
            
            <p style={{ opacity: 0.85 }}>Different sport. Same colors. Same result: Jackson State wins.</p>

            {/* Score Cards */}
            <div className="jsu-soccer-score-grid" data-testid="jsu-score-cards">
              <div className="jsu-soccer-score-card">
                <div className="jsu-soccer-score-label">Saturday — Football</div>
                <div className="jsu-soccer-score-value">35–17</div>
                <div className="jsu-soccer-score-teams">JACKSON STATE over SOUTHERN</div>
              </div>
              <div className="jsu-soccer-score-card">
                <div className="jsu-soccer-score-label">Sunday — Women's Soccer</div>
                <div className="jsu-soccer-score-value">3–0</div>
                <div className="jsu-soccer-score-teams">JACKSON STATE over SOUTHERN</div>
              </div>
            </div>
          </div>

          <h3 className="jsu-soccer-heading" style={{ fontSize: '1.5rem' }}>How the Soccer Shutout Happened</h3>
          
          <div data-testid="jsu-highlights">
            <div className="jsu-soccer-highlight-card">
              <div className="jsu-soccer-highlight-name">Kianah Farmer</div>
              <div className="jsu-soccer-highlight-desc">Opened the scoring in the 39th minute.</div>
            </div>
            <div className="jsu-soccer-highlight-card">
              <div className="jsu-soccer-highlight-name">Ryanne Hawthorn-Harper</div>
              <div className="jsu-soccer-highlight-desc">Scored in the 50th minute and later assisted on the third goal.</div>
            </div>
            <div className="jsu-soccer-highlight-card">
              <div className="jsu-soccer-highlight-name">Akeelah Mirza</div>
              <div className="jsu-soccer-highlight-desc">Made it 3-0 in the 62nd minute.</div>
            </div>
          </div>

          <p className="jsu-soccer-text" style={{ marginTop: '1.5rem' }}>
            Jackson State finished with <strong>20 shots</strong>, while goalkeeper <strong>Taylor Osborn</strong> made six saves to preserve the clean sheet.
          </p>

          {/* Action Photo - Southern Game */}
          <div className="jsu-soccer-inline-photo" data-testid="jsu-photo-southern">
            <img 
              src={actionPhotos.southern.src}
              alt={actionPhotos.southern.alt}
              loading="lazy"
            />
            <div className="jsu-soccer-inline-photo-credit">
              Photo: {actionPhotos.southern.credit}
            </div>
          </div>
        </section>

        {/* SECTION 4: ROSTER & COACHES */}
        <section id="jsu-roster" className="jsu-soccer-section" data-testid="jsu-section-roster">
          <h2 className="jsu-soccer-heading">Meet the 2026 Jackson State Tigers</h2>
          
          <p className="jsu-soccer-text">
            This roster helped carry Jackson State from a demanding non-conference schedule into the SWAC race. Scroll horizontally through the players, then meet the coaching staff.
          </p>

          {/* Roster Carousel */}
          <div className="jsu-soccer-carousel-wrapper" data-testid="jsu-roster-carousel">
            <div className="jsu-soccer-carousel-header">
              <div>
                <p className="jsu-soccer-carousel-label">2026 Women's Soccer</p>
                <h3 className="jsu-soccer-carousel-title">Roster</h3>
              </div>
              <div className="jsu-soccer-carousel-controls">
                <button
                  onClick={() => scrollCarousel(rosterRef, -1)}
                  className="jsu-soccer-carousel-btn"
                  aria-label="Scroll roster left"
                  data-testid="jsu-roster-prev"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => scrollCarousel(rosterRef, 1)}
                  className="jsu-soccer-carousel-btn"
                  aria-label="Scroll roster right"
                  data-testid="jsu-roster-next"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            
            <div ref={rosterRef} className="jsu-soccer-carousel" data-testid="jsu-roster-scroll">
              {players.map((player, idx) => (
                <div key={idx} className="jsu-soccer-player-card" data-testid={`jsu-player-${player.num}`}>
                  <div className="jsu-soccer-player-img">
                    <img 
                      src={player.img}
                      alt={`${player.name} - ${player.pos}`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.querySelector('.jsu-soccer-player-img-fallback').style.display = 'flex';
                      }}
                    />
                    <div className="jsu-soccer-player-img-fallback">
                      Official photo from<br />JSU Athletics
                    </div>
                  </div>
                  <div className="jsu-soccer-player-info">
                    <div className="jsu-soccer-player-meta">#{player.num} • {player.pos}</div>
                    <div className="jsu-soccer-player-name">{player.name}</div>
                    <div className="jsu-soccer-player-details">{player.year} • {player.hometown}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="jsu-soccer-credit">
              <strong>Roster photos:</strong> Jackson State Athletics / GoJSUTigers.com
            </p>
          </div>

          {/* Coaches Carousel */}
          <div className="jsu-soccer-carousel-wrapper" data-testid="jsu-coaches-carousel">
            <div className="jsu-soccer-carousel-header">
              <div>
                <p className="jsu-soccer-carousel-label">Leadership</p>
                <h3 className="jsu-soccer-carousel-title">The Staff Leading Thee Tigers</h3>
              </div>
              <div className="jsu-soccer-carousel-controls">
                <button
                  onClick={() => scrollCarousel(coachRef, -1)}
                  className="jsu-soccer-carousel-btn"
                  aria-label="Scroll coaches left"
                  data-testid="jsu-coaches-prev"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => scrollCarousel(coachRef, 1)}
                  className="jsu-soccer-carousel-btn"
                  aria-label="Scroll coaches right"
                  data-testid="jsu-coaches-next"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            
            <div ref={coachRef} className="jsu-soccer-carousel" data-testid="jsu-coaches-scroll">
              {coaches.map((coach, idx) => (
                <div key={idx} className="jsu-soccer-coach-card" data-testid={`jsu-coach-${idx}`}>
                  <div className="jsu-soccer-coach-img">
                    <img 
                      src={coach.img}
                      alt={`${coach.name} - ${coach.title}`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement.querySelector('.jsu-soccer-player-img-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="jsu-soccer-player-img-fallback">
                      Official coach photo from<br />JSU Athletics
                    </div>
                  </div>
                  <div className="jsu-soccer-coach-info">
                    <div className="jsu-soccer-coach-title">{coach.title}</div>
                    <div className="jsu-soccer-coach-name">{coach.name}</div>
                    <div className="jsu-soccer-coach-team">Jackson State Women's Soccer</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="jsu-soccer-credit">
              <strong>Coach Photos & Information:</strong> Jackson State Athletics / GoJSUTigers.com
            </p>
          </div>
        </section>

        {/* SOURCES */}
        <div className="jsu-soccer-sources" data-testid="jsu-sources">
          <h3 className="jsu-soccer-sources-title">Sources & Photo Credit</h3>
          <p className="jsu-soccer-sources-credit">
            <strong>Photos:</strong> Jackson State Soccer / Jackson State Athletics, GoJSUTigers.com.
          </p>
          <div className="jsu-soccer-sources-list">
            <a href="https://gojsutigers.com/sports/womens-soccer" target="_blank" rel="noopener noreferrer" className="jsu-soccer-sources-link">
              Jackson State Women's Soccer
            </a>
            <a href="https://gojsutigers.com/sports/womens-soccer/roster?view=3" target="_blank" rel="noopener noreferrer" className="jsu-soccer-sources-link">
              2026 Women's Soccer Roster
            </a>
            <a href="https://gojsutigers.com/news/2026/9/18/womens-soccer-tigers-defeat-austin-peay-at-home.aspx" target="_blank" rel="noopener noreferrer" className="jsu-soccer-sources-link">
              Austin Peay Recap
            </a>
            <a href="https://gojsutigers.com/news/2026/9/20/womens-soccer-jackson-state-races-past-middle-tennessee-to-close-out-the-non-conference-slate.aspx" target="_blank" rel="noopener noreferrer" className="jsu-soccer-sources-link">
              Middle Tennessee Recap
            </a>
            <a href="https://gojsutigers.com/news/2026/9/27/womens-soccer-jackson-state-rolls-past-southern-in-swac-opener.aspx" target="_blank" rel="noopener noreferrer" className="jsu-soccer-sources-link">
              Southern / SWAC Opener Recap
            </a>
          </div>
        </div>

        {/* Back to News / Related Articles */}
        <div style={{ 
          marginTop: '3rem', 
          padding: '2rem', 
          background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)', 
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Continue Reading
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <a 
              href="/news" 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: '#007AFF', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '9999px', 
                fontWeight: 'bold', 
                textTransform: 'uppercase',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'transform 0.2s'
              }}
              data-testid="back-to-news-btn"
            >
              📰 Back to News
            </a>
            <a 
              href="/boombox-classic" 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: 'transparent', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '9999px', 
                fontWeight: 'bold', 
                textTransform: 'uppercase',
                textDecoration: 'none',
                fontSize: '0.875rem',
                border: '2px solid white',
                transition: 'transform 0.2s'
              }}
              data-testid="related-article-btn"
            >
              🏈 BoomBox Classic 2026
            </a>
          </div>
          
          {/* Share Buttons */}
          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Share This Article</p>
            <ShareButtons 
              title="Jackson State Soccer: Built for the SWAC Race"
              url={typeof window !== 'undefined' ? window.location.href : ''}
              description="A comprehensive preview of the 2026 Jackson State Tigers women's soccer season. Roster breakdown, key players, and schedule analysis."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JacksonStateSoccerArticle;
